import React from 'react';
import { Box, Typography, Paper, TextField, InputAdornment, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Select, FormControl, InputLabel, Tabs, Tab } from '@mui/material';
import { Search, MoreVert, Add } from '@mui/icons-material';
import { fetchJobOffers, createJobOffer, updateJobOffer, deleteJobOffer, JOB_OFFER_STATUSES, fetchStressQuestionCategories } from '../services/recruitmentService';
import { RecruitJobOffer, RecruitJobOfferStatus } from '../types/recruitment';
import RichTextEditor from '../components/common/RichTextEditor';
import { listAll as listRodoAll, createDoc as createRodoDoc, updateDoc as updateRodoDoc, deleteDoc as deleteRodoDoc, uploadPdf as uploadRodoPdf } from '../services/rodoDocumentsService';
import CandidatesAllPage from './CandidatesAllPage';

const HumanResourcesPage: React.FC = () => {
	const [tab, setTab] = React.useState(0);
	const [q, setQ] = React.useState('');
	const [rows, setRows] = React.useState<RecruitJobOffer[]>([]);
	const [statusFilter, setStatusFilter] = React.useState<'' | RecruitJobOfferStatus>('');
	const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null);
	const [menuRow, setMenuRow] = React.useState<RecruitJobOffer | null>(null);
	const [addOpen, setAddOpen] = React.useState(false);
	const [form, setForm] = React.useState<Partial<RecruitJobOffer>>({ is_public: true, status: 'open' });
	const [saving, setSaving] = React.useState(false);
	const [stressCategories, setStressCategories] = React.useState<string[]>([]);

	// RODO – UI-only lista dokumentów (tymczasowo bez backendu)
	// Statusy w bazie: 'draft' | 'archiwalny' | 'opublikowany'
	type RodoDocStatus = 'draft' | 'archiwalny' | 'opublikowany' | 'publikuj'; // 'publikuj' to alias w formularzu → zapisujemy jako 'opublikowany'
	interface RodoDoc { id: number; slug: string; title_pl: string; status: RodoDocStatus; sort_order?: number | null; created_at: string; updated_at: string }
	const [rodoDocs, setRodoDocs] = React.useState<RodoDoc[]>([]);
	const [rodoStatusFilter, setRodoStatusFilter] = React.useState<'' | 'draft' | 'archiwalny' | 'opublikowany'>('');
	const [rodoMenuPos, setRodoMenuPos] = React.useState<{ top: number; left: number } | null>(null);
	const [rodoMenuRow, setRodoMenuRow] = React.useState<RodoDoc | null>(null);
	const [rodoAddOpen, setRodoAddOpen] = React.useState(false);
	const [rodoForm, setRodoForm] = React.useState<Partial<RodoDoc>>({ status: 'draft' });
	const rodoFileInputRef = React.useRef<HTMLInputElement | null>(null);

	const load = React.useCallback(async () => {
		try { const data = await fetchJobOffers(); setRows(data || []); } catch { setRows([]); }
	}, []);
	React.useEffect(() => { load(); }, [load]);
	React.useEffect(() => { (async()=>{ try { const cats = await fetchStressQuestionCategories(); setStressCategories(cats); } catch {} })(); }, []);
	// Załaduj RODO dokumenty
	React.useEffect(() => {
	  (async () => {
	    try { const docs: any[] = await listRodoAll(); setRodoDocs(docs as any); } catch {}
	  })();
	}, []);

	const filtered = rows.filter(r => {
		const hay = `${r.id} ${r.title_pl || ''} ${r.slug}`.toLowerCase();
		const matchesQ = hay.includes(q.toLowerCase());
		const matchesStatus = statusFilter ? r.status === statusFilter : true;
		return matchesQ && matchesStatus;
	});

	const openAdd = (row?: RecruitJobOffer) => {
		if (row) {
			setForm({ id: row.id, slug: row.slug, title_pl: row.title_pl || '', title_en: row.title_en, description_md: row.description_md || '', requirements_md: row.requirements_md || '', benefits_md: row.benefits_md || '', is_public: row.is_public, status: row.status, work_place: row.work_place || '', salary: row.salary || '', stress_category: (row as any).stress_category || '' });
		} else {
			setForm({ is_public: true, status: 'open', stress_category: '' });
		}
		setAddOpen(true);
	};

	// Kopia do edycji (nie zapisuje do SQL do czasu kliknięcia Zapisz)
	const openCopy = (row: RecruitJobOffer) => {
		const existing = new Set(rows.map(r => r.slug));
		const base = String(row.slug || 'copy');
		let candidate = `${base}-copy`;
		let n = 2;
		while (existing.has(candidate)) { candidate = `${base}-copy-${n++}`; if (n > 50) { candidate = `${base}-${Date.now()}`; break; } }
		setForm({
			// brak id => tryb Dodaj
			slug: candidate,
			title_pl: row.title_pl || '',
			title_en: row.title_en,
			description_md: row.description_md || '',
			requirements_md: row.requirements_md || '',
			benefits_md: row.benefits_md || '',
			is_public: row.is_public,
			status: row.status,
			work_place: row.work_place || '',
			salary: row.salary || ''
		});
		setAddOpen(true);
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			if (form.id) {
				await updateJobOffer(form.id as number, form);
			} else {
				await createJobOffer(form);
			}
			setAddOpen(false);
			await load();
		} catch {
			// fallback UI-only
		} finally { setSaving(false); }
	};

	const handleDelete = async () => {
		if (!menuRow) return;
		const ok = window.confirm('Usunąć ofertę pracy?');
		if (!ok) return;
		try { await deleteJobOffer(menuRow.id); await load(); } catch {}
		setMenuPos(null); setMenuRow(null);
	};

	return (
		<Box sx={{ p: 2 }}>
			<Tabs value={tab} onChange={(_, v)=>setTab(v)} aria-label="Zakładki HR" sx={{ mb: 2 }}>
				<Tab label="Rekrutowane stanowiska" />
				<Tab label="Kandydaci" />
				<Tab label="Pracownicy" />
				<Tab label="RODO" />
			</Tabs>

			{tab === 0 && (
				<>
					<Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 2, gap: 2, flexWrap:'wrap' }}>
						<Typography variant="h5" sx={{ fontWeight: 700 }}>HR — Rekrutowane stanowiska</Typography>
						<Button variant="contained" startIcon={<Add />} onClick={()=>openAdd()}>Dodaj rekrutowane stanowisko</Button>
					</Box>

					<Box sx={{ display:'flex', gap: 1, alignItems:'center', mb: 2 }}>
						<TextField size="small" placeholder="Szukaj" value={q} onChange={(e)=>setQ(e.target.value)} sx={{ width: 220 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }} />
						<FormControl size="small" sx={{ width: 180 }}>
							<InputLabel id="hr-status-filter-label" shrink>Status</InputLabel>
							<Select labelId="hr-status-filter-label" label="Status" value={statusFilter || ''} displayEmpty renderValue={(v)=> (v ? String(v) : 'Wszystkie')} onChange={(e)=> setStatusFilter((e.target.value as RecruitJobOfferStatus) || '')}>
								<MenuItem value=""><em>Wszystkie</em></MenuItem>
								{JOB_OFFER_STATUSES.map(s => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
							</Select>
						</FormControl>
					</Box>

					<TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', maxHeight: 520, overflowY: 'auto' }}>
						<Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { padding: '4px 6px' } }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ width: 100 }}>ID</TableCell>
									<TableCell sx={{ width: 140 }}>Data</TableCell>
									<TableCell>Nazwa stanowiska</TableCell>
									<TableCell sx={{ width: 220 }}>Miejsce pracy</TableCell>
									<TableCell sx={{ width: 180 }}>Wynagrodzenie</TableCell>
									<TableCell sx={{ width: 160 }}>Liczba kandydatów</TableCell>
									<TableCell sx={{ width: 120 }}>Status</TableCell>
									<TableCell align="right" sx={{ width: 44 }}></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filtered.map((r) => (
									<TableRow key={r.id} hover onClick={()=>{ window.location.href = `/hr/offers/${r.id}/candidates`; }} sx={{ cursor:'pointer' }}>
										<TableCell>{r.id}</TableCell>
										<TableCell>{String(r.created_at).slice(0,10)}</TableCell>
										<TableCell>{r.title_pl || r.title_en}</TableCell>
										<TableCell>{r.work_place || '—'}</TableCell>
										<TableCell>{r.salary || '—'}</TableCell>
										<TableCell>{(r as any).candidates_count ?? 0}</TableCell>
										<TableCell>
											<Chip size="small" label={r.status} color={r.status==='open'?'success':r.status==='paused'?'warning':r.status==='closed'?'default':'info'} />
										</TableCell>
										<TableCell align="right">
											<IconButton size="small" onClick={(e)=>{ e.stopPropagation(); setMenuRow(r); setMenuPos({ top: e.clientY, left: e.clientX }); }}>
												<MoreVert fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					<Menu anchorReference="anchorPosition" anchorPosition={menuPos ? { top: menuPos.top, left: menuPos.left } : undefined} open={Boolean(menuPos)} onClose={()=>{ setMenuPos(null); setMenuRow(null); }}>
						<MenuItem onClick={()=>{ if(menuRow) openAdd(menuRow); setMenuPos(null); }}>Edytuj</MenuItem>
						<MenuItem onClick={()=>{ if(menuRow) openCopy(menuRow); setMenuPos(null); }}>Kopiuj</MenuItem>
						<MenuItem onClick={()=>{ /* pokaż kandydatów - placeholder */ setMenuPos(null); }}>Pokaż kandydatów</MenuItem>
						<MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>Usuń</MenuItem>
					</Menu>

					<Dialog open={addOpen} onClose={()=>setAddOpen(false)} maxWidth="lg" fullWidth>
						<DialogTitle>{form.id ? 'Edytuj rekrutowane stanowisko' : 'Dodaj rekrutowane stanowisko'}</DialogTitle>
						<DialogContent dividers sx={{ pt: 2 }}>
							<Box sx={{ display:'grid', gridTemplateColumns:{ xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
								<TextField label="Skrót na stronę rekrutacji: Slug" value={form.slug || ''} onChange={(e)=>setForm(prev=>({ ...prev, slug: e.target.value }))} fullWidth size="small" />
								<TextField label="Nazwa stanowiska (title_pl)" value={form.title_pl || ''} onChange={(e)=>setForm(prev=>({ ...prev, title_pl: e.target.value }))} fullWidth size="small" />
								<FormControl size="small" fullWidth>
									<InputLabel>Status</InputLabel>
									<Select native label="Status" value={form.status || 'draft'} onChange={(e)=>setForm(prev=>({ ...prev, status: e.target.value as RecruitJobOfferStatus }))}>
										{JOB_OFFER_STATUSES.map(s => (<option key={s} value={s}>{s}</option>))}
									</Select>
								</FormControl>
								<FormControl size="small" fullWidth>
									<InputLabel id="hr-stress-cat-label" shrink>Kategoria stress</InputLabel>
									<Select
										labelId="hr-stress-cat-label"
										label="Kategoria stress"
										value={(form as any).stress_category || ''}
										onChange={(e)=>setForm(prev=>({ ...(prev||{}), stress_category: (e.target as any).value }))}
										displayEmpty
										renderValue={(v)=> v ? String(v) : '— wybierz —'}
									>
										{stressCategories.map(c => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
									</Select>
								</FormControl>
								<TextField label="Miejsce pracy (work_place)" value={(form.work_place || '') as any} onChange={(e)=>setForm(prev=>({ ...prev, work_place: e.target.value }))} fullWidth size="small" placeholder="np. Praca zdalna / woj. mazowieckie / Białystok" />
								<TextField label="Wynagrodzenie (salary)" value={(form.salary || '') as any} onChange={(e)=>setForm(prev=>({ ...prev, salary: e.target.value }))} fullWidth size="small" placeholder="np. 8 000 - 10 000 PLN" />
							</Box>

							<Box sx={{ display:'grid', gridTemplateColumns:{ xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
								<Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
									<Typography variant="subtitle2" sx={{ mb: 0.5 }}>Opis stanowiska (HTML)</Typography>
									<RichTextEditor value={form.description_md || ''} onChange={(html)=>setForm(prev=>({ ...prev, description_md: html }))} minHeight={220} bulletImageUrl={'/uploads/recruit/check_bullet.png'} />
								</Box>
								<Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
									<Typography variant="subtitle2" sx={{ mb: 0.5 }}>Wymagania (HTML)</Typography>
									<RichTextEditor value={form.requirements_md || ''} onChange={(html)=>setForm(prev=>({ ...prev, requirements_md: html }))} minHeight={220} bulletImageUrl={'/uploads/recruit/check_bullet.png'} />
								</Box>
								<Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
									<Typography variant="subtitle2" sx={{ mb: 0.5 }}>Oferujemy (HTML)</Typography>
									<RichTextEditor value={form.benefits_md || ''} onChange={(html)=>setForm(prev=>({ ...prev, benefits_md: html }))} minHeight={200} bulletImageUrl={'/uploads/recruit/check_bullet.png'} />
								</Box>
							</Box>
						</DialogContent>
						<DialogActions sx={{ justifyContent:'space-between' }}>
							{form.id ? (
								<Button color="error" onClick={async()=>{ if(!form.id) return; const ok = window.confirm('Usunąć ofertę pracy?'); if(!ok) return; try { await deleteJobOffer(form.id as number); setAddOpen(false); await load(); } catch {} }}>Usuń</Button>
							) : <span />}
							<Box>
								<Button onClick={()=>setAddOpen(false)} disabled={saving}>Anuluj</Button>
								<Button variant="contained" onClick={handleSave} disabled={saving} sx={{ ml: 1 }}>{saving ? 'Zapisywanie…' : 'Zapisz'}</Button>
							</Box>
						</DialogActions>
					</Dialog>
				</>
			)}

			{tab === 1 && (
				<Box sx={{ p: 0 }}>
					<CandidatesAllPage />
				</Box>
			)}

			{tab === 2 && (
				<Box sx={{ p: 1 }}>
					<Typography>W przygotowaniu: pracownicy</Typography>
				</Box>
			)}

			{tab === 3 && (
				<>
					<Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 2, gap: 2, flexWrap:'wrap' }}>
						<Typography variant="h5" sx={{ fontWeight: 700 }}>HR — RODO (dokumenty)</Typography>
						<Button variant="contained" startIcon={<Add />} onClick={()=>{ setRodoForm({ status: 'draft' }); setRodoAddOpen(true); }}>Dodaj dokument</Button>
					</Box>
					<Typography variant="body2" sx={{ mb: 2 }}>
						<span style={{ color: '#c53030', fontWeight: 600 }}>Dokumenty ze statusem opublikowany są widoczne publicznie na stronie</span> <a href="/documents" target="_blank" rel="noreferrer">app.unitresco.com/documents</a>.
					</Typography>

					<Box sx={{ display:'flex', gap: 1, alignItems:'center', mb: 2 }}>
						<TextField size="small" placeholder="Szukaj" value={(rodoForm as any).q || ''} onChange={(e)=> setRodoForm(prev => ({ ...(prev||{}), q: e.target.value }))} sx={{ width: 220 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }} />
						<FormControl size="small" sx={{ width: 220 }}>
							<InputLabel id="hr-rodo-status-filter-label" shrink>Status</InputLabel>
							<Select labelId="hr-rodo-status-filter-label" label="Status" value={rodoStatusFilter || ''} displayEmpty renderValue={(v)=> (v ? String(v) : 'Wszystkie')} onChange={(e)=> setRodoStatusFilter((e.target.value as any) || '')}>
								<MenuItem value=""><em>Wszystkie</em></MenuItem>
								{(['draft','opublikowany','archiwalny'] as const).map(s => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
							</Select>
						</FormControl>
					</Box>

					<TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', maxHeight: 520, overflowY: 'auto' }}>
						<Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { padding: '4px 6px' } }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ width: 100 }}>ID</TableCell>
									<TableCell sx={{ width: 140 }}>Data</TableCell>
									<TableCell>Tytuł</TableCell>
									<TableCell sx={{ width: 120 }}>Kolejność</TableCell>
									<TableCell sx={{ width: 120 }}>Status</TableCell>
									<TableCell align="right" sx={{ width: 44 }}></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{(rodoDocs
									.filter(d => (rodoForm as any).q ? (d.title_pl || '').toLowerCase().includes(String((rodoForm as any).q).toLowerCase()) : true)
									.filter(d => rodoStatusFilter ? d.status === rodoStatusFilter : true)
								).map((d) => (
									<TableRow key={d.id} hover>
										<TableCell>{d.id}</TableCell>
										<TableCell>{String(d.created_at).slice(0,10)}</TableCell>
										<TableCell>{d.title_pl}</TableCell>
										<TableCell>{(d as any).sort_order ?? '—'}</TableCell>
										<TableCell><Chip size="small" label={d.status} color={d.status==='opublikowany'?'success':d.status==='draft'?'info':'default'} /></TableCell>
										<TableCell align="right">
											<IconButton size="small" onClick={(e)=>{ e.stopPropagation(); setRodoMenuRow(d); setRodoMenuPos({ top: e.clientY, left: e.clientX }); }}>
												<MoreVert fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					<Menu anchorReference="anchorPosition" anchorPosition={rodoMenuPos ? { top: rodoMenuPos.top, left: rodoMenuPos.left } : undefined} open={Boolean(rodoMenuPos)} onClose={()=>{ setRodoMenuPos(null); setRodoMenuRow(null); }}>
						<MenuItem onClick={()=>{ if(rodoMenuRow){ setRodoForm(rodoMenuRow); setRodoAddOpen(true);} setRodoMenuPos(null); }}>Edytuj</MenuItem>
						<MenuItem onClick={()=>{ if(rodoMenuRow){ const now = new Date().toISOString(); const copy = { ...rodoMenuRow, id: 0, title_pl: `${rodoMenuRow.title_pl} (kopia)`, created_at: now, updated_at: now } as any; setRodoForm(copy); setRodoAddOpen(true);} setRodoMenuPos(null); }}>Kopiuj</MenuItem>
						<MenuItem onClick={async()=>{ if(rodoMenuRow){ if(window.confirm('Usunąć dokument?')) { try { await deleteRodoDoc(rodoMenuRow.id as any); setRodoDocs(prev=>prev.filter(x=>x.id!==rodoMenuRow.id)); } catch {} } } setRodoMenuPos(null); }} sx={{ color: 'error.main' }}>Usuń</MenuItem>
					</Menu>

					<Dialog open={rodoAddOpen} onClose={()=>setRodoAddOpen(false)} maxWidth="sm" fullWidth>
						<DialogTitle>{(rodoForm as any).id ? 'Edytuj dokument RODO' : 'Dodaj dokument RODO'}</DialogTitle>
						<DialogContent dividers>
							<Box sx={{ display:'grid', gridTemplateColumns:'1fr', gap: 2 }}>
								<TextField size="small" label="Tytuł (PL)" value={rodoForm.title_pl || ''} onChange={(e)=>setRodoForm(prev=>({ ...(prev||{}), title_pl: e.target.value }))} fullWidth />
								<TextField size="small" label="Slug" value={rodoForm.slug || ''} onChange={(e)=>setRodoForm(prev=>({ ...(prev||{}), slug: e.target.value }))} fullWidth />
								<TextField size="small" label="Kolejność (liczba)" type="number" value={(rodoForm as any).sort_order ?? ''} onChange={(e)=>setRodoForm(prev=>({ ...(prev||{}), sort_order: e.target.value === '' ? null : Number(e.target.value) }))} fullWidth />
								<FormControl size="small" fullWidth>
									<InputLabel>Status</InputLabel>
									<Select native label="Status" value={rodoForm.status || 'draft'} onChange={(e)=>setRodoForm(prev=>({ ...(prev||{}), status: e.target.value as any }))}>
										<option value="draft">draft</option>
										<option value="publikuj">publikuj</option>
										<option value="archiwalny">archiwalny</option>
									</Select>
								</FormControl>
							</Box>
							<Box>
								<Typography variant="subtitle2" sx={{ mb: 0.5 }}>Plik PDF</Typography>
								<Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
									<Button variant="outlined" size="small" onClick={()=>rodoFileInputRef.current?.click()}>Wybierz plik PDF</Button>
									{ (rodoForm as any).file_url && (
										<Typography variant="body2" color="text.secondary" sx={{ ml: 1, overflow:'hidden', textOverflow:'ellipsis' }}>{(rodoForm as any).file_url}</Typography>
									) }
								</Box>
								<input ref={rodoFileInputRef} type="file" accept="application/pdf" style={{ display:'none' }} onChange={async (e)=>{
									const file = (e.target.files && e.target.files[0]) || null;
									e.currentTarget.value = '';
									if (!file) return;
									try {
										const up = await uploadRodoPdf(file);
										if (up?.success && up.file_url) {
											setRodoForm(prev=>({ ...(prev||{}), file_url: up.file_url } as any));
										}
									} catch {}
								}} />
							</Box>
						</DialogContent>
						<DialogActions>
							<Button onClick={()=>setRodoAddOpen(false)}>Anuluj</Button>
							<Button variant="contained" onClick={async()=>{
  const now = new Date().toISOString();
  const normalizedStatus = (rodoForm.status === 'publikuj' ? 'opublikowany' : rodoForm.status) as 'draft'|'archiwalny'|'opublikowany';
  try {
    if ((rodoForm as any).id) {
      await updateRodoDoc((rodoForm as any).id as any, { slug: rodoForm.slug as any, title_pl: rodoForm.title_pl as any, file_url: (rodoForm as any).file_url as any, status: rodoForm.status as any, sort_order: (rodoForm as any).sort_order as any });
      setRodoDocs(prev=>prev.map(x=> x.id === (rodoForm as any).id ? ({ ...(x as any), ...rodoForm, status: normalizedStatus, updated_at: now } as any) : x));
    } else {
      const created = await createRodoDoc({ slug: (rodoForm.slug as any) || `doc-${Date.now()}`, title_pl: (rodoForm.title_pl as any) || 'Dokument', file_url: (rodoForm as any).file_url as any, status: rodoForm.status as any, sort_order: (rodoForm as any).sort_order as any });
      setRodoDocs(prev=>[created as any, ...prev]);
    }
  } catch {}
  setRodoAddOpen(false);
}}>Zapisz</Button>
						</DialogActions>
					</Dialog>
				</>
			)}
		</Box>
	);
};

export default HumanResourcesPage;


